import { api, apiBytes } from "./client";

/*
 * AI features (server/ai*.js, docs/implemented-ai-assist.md): receipt and bill scanning into the Scan inbox, and the assistant.
 * Everything is off and hidden unless the server has an Anthropic API key and this organization's Admin switched AI on.
 */

export type AiStatus =
  | { available: false }
  | { available: true; mock: boolean; enabled: boolean; scan: boolean; assistant: boolean; can_manage: boolean; can_scan: boolean; used_percent: number; cap_reached: boolean };

export type AiUsage = {
  month: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost: string;
  cap: string;
  used_percent: number;
  cap_reached: boolean;
  by_purpose: { purpose: string; calls: number; input_tokens: number; output_tokens: number; cost: string }[];
  history: { month: string; calls: number; input_tokens: number; output_tokens: number; cost: string }[];
  currency: "USD";
  note: string;
};

export type AiSettings = {
  available: boolean;
  mock: boolean;
  model: string;
  enabled: boolean;
  scan_enabled: boolean;
  assistant_enabled: boolean;
  monthly_cap: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  disclosure: string[];
  usage: AiUsage;
};
export type AiSettingsIn = Partial<{ enabled: boolean; scan_enabled: boolean; assistant_enabled: boolean; monthly_cap: string; acknowledged: boolean }>;

export type ScanStatus = "processing" | "ready" | "failed" | "saved" | "discarded";
export type ScanFlag = { level: "error" | "warning"; code: string; message: string };
export type ScanRow = {
  id: string;
  status: ScanStatus;
  kind: "expense" | "bill" | null;
  file_name: string;
  content_type: string;
  size: number;
  source_attachment_id: string | null;
  error: string | null;
  record_entity: "expenses" | "bills" | null;
  record_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  vendor_name: string | null;
  total: string | null;
  date: string | null;
  errors: number;
  warnings: number;
};
export type ExpenseDraft = { date: string; vendor_id: string | null; account_id: string | null; amount: string; gst_rate: number; hsn_sac: string | null; reference: string | null; notes: string };
export type BillLineDraft = { description: string; account_id: string | null; quantity: string; rate: string; gst_rate: number; hsn_sac: string | null };
export type BillDraft = { vendor_id: string | null; bill_number: string; date: string; due_date: string | null; tax_inclusive: boolean; reference: string | null; notes: string; lines: BillLineDraft[] };
export type ScanDetail = ScanRow & {
  fields: Record<string, unknown> & { vendor_name: string; vendor_gstin: string; invoice_number: string; invoice_date: string; lines: Record<string, string>[] };
  flags: ScanFlag[];
  vendor: { id: string; name: string; by: "gstin" | "name"; score: number } | null;
  vendor_candidates: { id: string; name: string; score: number }[];
  drafts: { suggested: "expense" | "bill"; expense: ExpenseDraft; bill: BillDraft } | null;
  model: string | null;
  read_at: string | null;
};

export type ChatTurn = { role: "user" | "assistant"; text: string };
export type ToolCall = { name: string; label: string; status: "done" | "denied" | "error" };
export type Proposal =
  | { type: "reminder_email"; invoice_id: string; invoice_number: string | null; customer_name: string | null; email: string | null; balance: string | null; due_date: string | null; subject: string; body: string }
  | { type: "customer_note"; customer_id: string; customer_name: string | null; note: string }
  | { type: "bank_category"; transaction_id: string; date: string; description: string | null; amount: string; kind: string; account_id: string; account_name: string; reason: string };
export type AskReply = { answer: string; tool_calls: ToolCall[]; proposals: Proposal[]; usage: { input_tokens: number; output_tokens: number } };

const A = "/admin/ai";

export const ai = {
  status: () => api<AiStatus>("GET", `${A}/status`),
  settings: () => api<AiSettings>("GET", `${A}/settings`),
  saveSettings: (body: AiSettingsIn) => api<AiSettings>("PUT", `${A}/settings`, body),
  usage: () => api<AiUsage>("GET", `${A}/usage`),

  scans: (status: "open" | "done" | "all" = "open") => api<ScanRow[]>("GET", `${A}/scans?status=${status}`),
  scan: (id: string) => api<ScanDetail>("GET", `${A}/scans/${id}`),
  /** A photo or PDF, as the raw body (like an attachment upload). */
  upload: async (file: Blob, name: string, kind?: "expense" | "bill") => {
    const q = new URLSearchParams({ name, ...(kind ? { kind } : {}) });
    const res = await apiBytes("POST", `${A}/scans?${q}`, file, { "Content-Type": "application/octet-stream" });
    return (await res.json()) as ScanRow;
  },
  file: async (id: string) => (await apiBytes("GET", `${A}/scans/${id}/file`)).blob(),
  retry: (id: string) => api<ScanRow>("POST", `${A}/scans/${id}/retry`, {}),
  discard: (id: string) => api<ScanRow>("POST", `${A}/scans/${id}/discard`, {}),
  link: (id: string, entity: "expenses" | "bills", record_id: string) => api<ScanRow>("POST", `${A}/scans/${id}/link`, { entity, record_id }),

  ask: (question: string, history: ChatTurn[]) => api<AskReply>("POST", `${A}/assistant/ask`, { question, history }),
};

/* The status is read once per page load and shared (the nav, the top bar, Settings); a change in Settings › AI refreshes it. */
let cached: Promise<AiStatus> | null = null;
export const AI_CHANGED = "crm:ai-changed";
export function aiStatus(): Promise<AiStatus> {
  cached ??= ai.status().catch(() => ({ available: false }) as AiStatus);
  return cached;
}
export function aiChanged() {
  cached = null;
  window.dispatchEvent(new Event(AI_CHANGED));
}
